import styled, { css } from "styled-components";
import { brand } from "../../../theme/tokens";

/**
 * Styleds for the "Add to Segment" picker. Kept in their own file so the
 * dialog's behaviour (SegmentPickerDialog.jsx) and its appearance stay
 * separately readable and separately reviewable.
 *
 * Design-system conformance, deliberately, not by accident:
 *  - brand purple 700 (#6F27D6) for the primary action and the checked state,
 *    the same value the Capture drawer's own controls already use;
 *  - the app's standard focus ring, read from tokens.brand.focusRing (never
 *    re-hardcoded) — #CBB4FE measured 1.83:1 and failed WCAG 1.4.11's 3:1 floor;
 *  - >=32px interactive height everywhere (the app's touch-target ruling);
 *  - `prefers-reduced-motion` honoured on every transition — an animation that
 *    ignores it is a bug wearing a flourish's clothes.
 */

const PURPLE_700 = "#6F27D6";
// The focus ring is NOT a local constant any more — it reads the single source
// of truth so it can never drift from the rest of the app again.
const FOCUS_RING = brand.focusRing;
const INK = "#123051";

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    animation: none;
  }
`;

export const PickerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(18, 48, 81, 0.35);
  z-index: 1400;
`;

export const PickerPanel = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(420px, calc(100vw - 32px));
  /* MANY-segments state: the panel never grows past the viewport; the list
     scrolls inside its own container instead (see SegmentList). */
  max-height: min(560px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(18, 48, 81, 0.22);
  z-index: 1401;
  overflow: hidden;
`;

export const PickerHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #eceaf3;
`;

export const PickerBody = styled.div`
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;

export const SegmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* Overflow scrolls HERE, so a wheel with many segments never pushes the
     footer buttons off-screen. */
  overflow-y: auto;
  max-height: 320px;
`;

export const SegmentOption = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ $checked }) => ($checked ? PURPLE_700 : "#E3E0EC")};
  background: ${({ $checked }) => ($checked ? "#F7F2FF" : "#ffffff")};
  color: ${INK};
  font: 500 14px "DM Sans", system-ui, sans-serif;
  text-align: left;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;
  ${reducedMotion}

  /* LONG-name state: one line, truncated with an ellipsis, never wrapping the
     row into two lines or overflowing the panel. */
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: #f7f2ff;
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${FOCUS_RING};
  }
`;

// QA-1095: the segment's own colour, shown as a small swatch on each row so the
// picker matches the coloured wedge on the wheel. A hairline border keeps a very
// pale or white swatch visible against the white row; aria-hidden because the
// row's text label already carries the segment identity for assistive tech.
export const SegmentColorSwatch = styled.span`
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $color }) => $color || "#E3E0EC"};
  border: 1px solid rgba(18, 48, 81, 0.18);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
`;

export const SegmentRadioDot = styled.span`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ $checked }) => ($checked ? PURPLE_700 : "#B9B3C9")};
  background: ${({ $checked }) =>
    $checked
      ? `radial-gradient(circle at center, ${PURPLE_700} 0 4px, transparent 5px)`
      : "transparent"};
  transition: border-color 120ms ease;
  ${reducedMotion}
`;

export const PickerNotice = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  font: 400 13px "DM Sans", system-ui, sans-serif;
  background: ${({ $error }) => ($error ? "#FDEDED" : "#F4F2F9")};
  color: ${({ $error }) => ($error ? "#8B1F1F" : "#4A4560")};
`;

export const PickerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px 16px;
  border-top: 1px solid #eceaf3;
`;

const buttonBase = css`
  min-height: 36px;
  padding: 8px 16px;
  border-radius: 8px;
  font: 600 13px "DM Sans", system-ui, sans-serif;
  cursor: pointer;
  transition: background 120ms ease;
  ${reducedMotion}

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${FOCUS_RING};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const PickerButton = styled.button`
  ${buttonBase}
  border: 1px solid ${PURPLE_700};
  background: ${PURPLE_700};
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #5c1fb5;
  }
`;

export const PickerGhostButton = styled.button`
  ${buttonBase}
  border: 1px solid #D8D4E4;
  background: #ffffff;
  color: ${INK};

  &:hover {
    background: #f4f2f9;
  }
`;
